import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { Badge, Card, Pager, PanelState } from "../components/ui";
import { dateTime, num, titleCase, usd } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { PageMeta, RecentRender, RenderColor } from "../lib/types";

function hexOf(color: RenderColor): string | null {
  const raw = color.hexCode ?? color.hex;
  return raw && /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : null;
}

/**
 * ── Useful in a way no chart is ────────────────────────────────────────────
 * A broken render pipeline shows up here as a page of grey rectangles long
 * before it shows up as a support ticket. The failure modes that matter — a
 * model returning washed-out walls, the wrong shade landing on the ceiling,
 * every image coming back identical — are things you SEE and cannot count.
 */
export function RenderWallPage() {
  const [page, setPage] = useState(1);

  const { data, error, loading, stale, reload } = useApi<{
    renders: RecentRender[];
    pagination: PageMeta;
  }>("/api/admin/renders", { page, pageSize: 48 });

  return (
    <Page
      title="Render wall"
      subtitle={
        data
          ? `${num(data.pagination.total)} views generated, newest first`
          : "Loading…"
      }
      actions={
        <button className="btn btn-sm" onClick={reload}>
          Refresh
        </button>
      }
    >
      <Card
        title="Latest views"
        note="What the model is actually producing right now. Charts will tell you renders are happening; only this tells you they are any good."
      >
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.renders.length === 0}
            emptyText="No views have been generated yet."
          >
            <div className="render-wall">
              {data?.renders.map((r) => (
                <figure key={r.id} className="render-tile" style={{ margin: 0 }}>
                  <img
                    src={r.imageUrl}
                    alt={`${r.project.name} — generated ${dateTime(r.createdAt)}`}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                  <figcaption className="render-meta">
                    <div
                      style={{
                        color: "var(--text)",
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {r.project.name}
                    </div>
                    <div>
                      {titleCase(r.project.roomType)} ·{" "}
                      {titleCase(r.finishId ?? "matte")} ·{" "}
                      {titleCase(r.lightingId)}
                    </div>
                    <div>{dateTime(r.createdAt)}</div>
                    {r.ai && (
                      <div title="Tokens the model reported for this view, and its estimated cost">
                        {num(r.ai.totalTokens)} tokens · {usd(r.ai.costUsd)}
                      </div>
                    )}
                    {r.user && (
                      <Link
                        to={`/customers/${r.project.userId}`}
                        style={{ textDecoration: "underline" }}
                      >
                        {r.user.fullName ?? "Unnamed"}
                      </Link>
                    )}
                    {r.downloads > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Badge tone="good">
                          downloaded {num(r.downloads)}×
                        </Badge>
                      </div>
                    )}
                    <span className="render-colors">
                      {r.colors.slice(0, 10).map((c, i) => {
                        const hex = hexOf(c);
                        return hex ? (
                          <span
                            key={i}
                            style={{ background: hex }}
                            title={`${c.name ?? ""} ${c.code ?? hex}`}
                          />
                        ) : null;
                      })}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="views"
            />
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}
